import { useEffect, useState } from 'react'

import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setProductFilterData } from '../redux/slices/filter.slice'

import { initFlowbite } from 'flowbite'

import { categoryService, productService } from 'services'
import { brandsService } from 'services/brand.service'

import { formatPrice } from 'utils'
import { _object } from 'utils/interfaces'
import { routes } from 'utils/routes'

import { Pagination } from 'components'

import { EditIcon, ViewIcon } from 'assets/svg'
import { Button } from 'components/form-inputs'

const Products = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  const [results, setResults] = useState<_object>({})
  const [filterEntities, setFilterEntities] = useState<_object>({})

  const savedFilters = useSelector((state: _object) => state.filter.productFilter)

  const [filters, setFilters] = useState(
    Object.keys(savedFilters || {}).length ? savedFilters : {
      q: '',
      page: 1,
      per_page: 25,
      stock_status: '',
      filters: { status: '', category_id: '', brand_id: '', country: '' }
    }
  )

  useEffect(() => {
    categoryService.getCategories({ per_page: 9999 }).then((results: _object) => {
      setFilterEntities((prev) => ({
        ...prev,
        categories: results.items
      }))
    })

    brandsService.getBrands({ per_page: 9999 }).then((results: _object) => {
      setFilterEntities((prev) => ({
        ...prev,
        brands: results.items
      }))
    })

    productService.getCountries().then((results: _object) => {
      setFilterEntities((prev) => ({
        ...prev,
        countries: results.items
      }))
    })

    initFlowbite()
  }, [])

  useEffect(() => {
    setLoading(true)
    productService.getProducts(filters).then((results) => {
      setResults(results)
      setLoading(false)
    })
  }, [filters])

  useEffect(() => {
    dispatch(setProductFilterData(filters))
  }, [dispatch, filters])



  return (
    <>
      <div className="mb-6 flex justify-between">
        <div className='flex items-center'>
          <h1 className="text-2xl font-semibold">
            Products{' '}
            {results?.pagination?.total_items && (
              <small>Total records: {results?.pagination?.total_items}</small>
            )}
          </h1>
        </div>
        <div className='flex gap-4'>
          <Button
            label=' Add products'
            type="button"
            className="w-[100px] h-50 bg-purple-900 text-white text-sm rounded-md hover:bg-purple-950 transition"
            onClick={() => {
              navigate(`${routes.products}/add`)
            }}
          />

        </div>
      </div>

      <div className="mb-4 flex gap-4">
        <form>
          <div className="flex items-center gap-2">
            <input
              type="search"
              placeholder="Search keyword"
              className="w-60 rounded-md border px-3 py-2 text-sm outline-none focus:outline-none"
              value={filters.q || ''}
              onChange={(e) => {
                setFilters({ ...filters, q: e.target.value })
              }}
            />

            <div className="relative w-full">
              <select
                name="category"
                value={filters.filters?.category_id || ''}
                onChange={(e) => {
                  setFilters({ ...filters, filters: { ...filters.filters, category_id: e.target.value } })
                }}
                className="block w-full appearance-none rounded-md border bg-white px-3 py-2 pr-10 text-sm outline-none focus:outline-none"
              >
                <option value="">All categories</option>
                {filterEntities?.categories?.map((item: _object, i: number) => (
                  <option key={i} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>

              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            <div className="relative w-full">
              <select
                className="block w-full appearance-none rounded-md border bg-white px-3 py-2 pr-10 text-sm outline-none focus:outline-none"
                name="brand"
                value={filters.filters?.brand_id || ''}
                onChange={(e) => {
                  setFilters({ ...filters, filters: { ...filters.filters, brand_id: e.target.value } })
                }}
              >
                <option value="">All brands</option>
                {filterEntities?.brands?.map((item: _object, i: number) => {
                  return (
                    <option key={i} value={item.id}>
                      {item.title}
                    </option>
                  )
                })}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            <div className="relative w-full">
              <select
                className="block w-full appearance-none rounded-md border bg-white px-3 py-2 pr-10 text-sm outline-none focus:outline-none"
                name="country"
                value={filters.filters?.country || ''}
                onChange={(e) => {
                  setFilters({ ...filters, filters: { ...filters.filters, country: e.target.value } })
                }}
              >
                <option value="">All countries</option>
                {[
                  "India",
                  "United States",
                  "United Kingdom",
                  "Canada",
                  "Australia",
                  "Germany",
                  "France",
                  "Italy",
                  "Spain",
                  "Brazil",
                  "China",
                  "Japan",
                  "Russia",
                  "South Africa",
                  "Mexico",
                  "Indonesia",
                  "Saudi Arabia",
                  "Argentina"
                ]?.map((item, i: number) => {
                  return <option key={i} value={item}>{item}</option>
                })}
              </select>

              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            <div className="relative w-full">
              <select
                className="block w-full appearance-none rounded-md border bg-white px-3 py-2 pr-10 text-sm outline-none focus:outline-none"
                name="stock_status"
                value={filters.stock_status || ''}
                onChange={(e) => {
                  setFilters({ ...filters, stock_status: e.target.value })
                }}
              >
                <option value="">Stock status</option>
                {[
                  { key: 'In Stock', value: 'instock' },
                  { key: 'Out Of Stock', value: 'outofstock' }
                ]?.map((item: _object, i: number) => {
                  return (
                    <option key={i} value={item.value}>
                      {item.key}
                    </option>
                  )
                })}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>


            <button
              type="button"
              className="px-3 py-1 text-sm font-medium whitespace-nowrap text-red-600 underline"
              onClick={() =>
                setFilters({
                  q: "",
                  page: 1,
                  per_page: 25,
                  sort: "-created_at",
                  stock_status: '',
                  filters: { status: "", category_id: '', brand_id: '', country: '' },
                })
              }
            >
              Clear filters
            </button>


          </div>
        </form>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr className="text-left text-sm font-medium text-gray-900">
              <th className="px-4 py-3 font-semibold">#</th>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold"> Regular Price</th>
              <th className="px-4 py-3 font-semibold"> Discount Price</th>
              <th className="px-4 py-3 font-semibold">Stock</th>
              <th className="px-4 py-3 font-semibold">Category</th>
              <th className="px-4 py-3 font-semibold">Brand</th>
              <th className="px-4 py-3 font-semibold">Country</th>
              <th className="px-4 py-3 text-center font-semibold">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 text-sm">
            {loading === false ? (
              results?.items?.length > 0 &&
              results?.items?.map((item: _object, i: number) => {
                return (
                  <tr className="hover:bg-gray-50" key={i}>
                    <td className="px-4 py-3">  {(filters.page - 1) * filters.per_page + i + 1}</td>
                    <td className="px-4 py-3">{item?.name}</td>
                    <td className="px-4 py-3">{formatPrice(item?.regular_price)}</td>
                    <td className="px-4 py-3">{formatPrice(item?.discounted_price)}</td>
                    <td className="px-4 py-3">{item?.stock_status}</td>
                    <td className="px-4 py-3">{item?.category?.name || ''}</td>
                    <td className="px-4 py-3">{item?.brand?.title || ''}</td>
                    <td className="px-4 py-3">{item?.country || ''}</td>

                    <td className="text-center px-4 py-3">
                      <div className='flex h-full items-center justify-end gap-2'>
                        <Link
                          to={`https://treasuresunlimited.vercel.app/products/${item.id}`}
                          target="blank"
                          title="View Product Details"
                        >
                          <ViewIcon className="h-[20px] w-[20px]" />
                        </Link>
                        <Link to={`${routes.products}/${item.id}`} title="Edit Product">
                          <EditIcon />
                        </Link>
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <ProductSkeleton />
            )}
            {results?.items?.length <= 0 &&
              <td colSpan={12} className="text-center py-6 text-gray-500">
                <p>No record found</p>
              </td>
            }

          </tbody>
        </table>
      </div>

      {results?.pagination?.total_pages > 1 && loading === false && (
        <Pagination
          pageCount={results?.pagination?.total_pages}
          current_page={results?.pagination?.current_page}
          onPageChange={(e: _object) => {
            setFilters({ ...filters, page: e.selected + 1 })
          }}
        />
      )}
    </>
  )
}

export default Products

const ProductSkeleton = () => {
  return (
    <tr className="animate-pulse">
      <td className="px-4 py-3">
        <div className="h-4 w-6 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-32 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-12 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-8 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-24 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-20 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-12 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-12 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-12 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-12 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-12 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-12 rounded bg-gray-300"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 w-12 rounded bg-gray-300"></div>
      </td>
      <td className="flex justify-center gap-2 px-4 py-3">
        <div className="h-5 w-5 rounded-full bg-gray-300"></div>
        <div className="h-5 w-5 rounded-full bg-gray-300"></div>
      </td>
    </tr>
  )
}
